<template>
  <default-layout>
    <div>
      <v-data-table
        :headers="headers"
        :items="facilities"
        :items-per-page="limit"
        :server-items-length="serverItemsLength"
        :options.sync="paginationOptions"
        :loading="loading"
        :fixed-header="true"
        class="elevation-2"
        :multi-sort="false"
        :must-sort="true"
        sort-by="facilityId"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100]
        }"
        @update:options="updatePaginationOptions($event)"
      >
        <template v-slot:top>
          <v-row>
            <!-- SEARCH -->
            <v-col class="pl-7">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                @keyup.enter="
                  searchClicked = true;
                  search();
                "
                :disabled="search.length ? true : false"
              ></v-text-field>
            </v-col>

            <!-- DATE RANGE -->
            <v-col>
              <v-menu
                ref="menu"
                v-model="showDatePicker"
                :close-on-content-click="false"
                :return-value.sync="dates"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="Date range"
                    prepend-icon="mdi-calendar-range"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="dates" scrollable range no-title>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu = false"
                    >Cancel</v-btn
                  >
                  <v-btn text color="primary" @click="$refs.menu.save(dates)"
                    >OK</v-btn
                  >
                </v-date-picker>
              </v-menu>
            </v-col>

            <!-- NETWORK STATUS -->
            <v-col>
              <v-select
                :items="networkStatuses"
                item-text="name"
                v-model="networkStatus"
                label="Network Status"
              ></v-select>
            </v-col>

            <!-- ACCOUNT STATUS -->
            <v-col>
              <v-select
                :items="accountStatuses"
                item-text="name"
                v-model="accountStatus"
                label="Account Status"
                :disabled="networkStatus === 'out_of_network'"
              ></v-select>
            </v-col>

            <!-- PREMIUM SETTING -->
            <v-col>
              <v-select
                :items="premiumSettings"
                item-text="name"
                v-model="premiumSetting"
                label="Premium Setting"
                :disabled="
                  accountStatus === 'basic' ||
                    networkStatus === 'out_of_network'
                "
              ></v-select>
            </v-col>

            <!-- SEARCH BUTTON-->
            <v-col>
              <v-btn
                class="primary mt-3"
                @click="
                  searchClicked = true;
                  search();
                "
                >Search
              </v-btn>

              <json-c-s-v
                class="pl-2"
                style="display: inline-block"
                :data="csvData"
                :name="`${filename}.csv`"
                :labels="downloadLabels"
                :fields="[
                  'companyId',
                  'companyName',
                  'facilityId',
                  'name',
                  'uniqueUsers',
                  'pageViews',
                  'premiumClicks',
                  'premiumClickPrice',
                  'averagePremiumClickPrice',
                  'premiumCalls',
                  'averagePremiumCallPrice',
                  'subscription',
                  'subscriptionPrice',
                  'premiumStatus',
                  'daysSinceLastActivity',
                  'matchReferrals',
                  'matchFeaturedListingLeads',
                  'helplineCalls',
                  'getHelpReferrals',
                  'helplineCallsReferrals',
                  'contactUsClicks',
                  'contactUsCalls',
                  'freeValue'
                ]"
              >
                <v-btn
                  class="mt-3"
                  color="primary"
                  x-small
                  fab
                  :disabled="!facilities || !facilities.length"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </json-c-s-v>
            </v-col>
          </v-row>
        </template>
      </v-data-table>
    </div>
  </default-layout>
</template>

<script>
import { format, subMonths, isBefore, parseISO } from "date-fns";
import { getGlobalReport } from "../api/report";
import JsonCSV from "vue-json-csv";

export default {
  name: "Reporting",
  title: "Reporting",

  data() {
    return {
      offset: 0,
      limit: 10,
      loading: false,
      serverItemsLength: 0,
      expanded: [],
      routes: [],
      singleExpand: false,
      search: "",
      dates: [
        format(subMonths(new Date(), 12), "yyyy-MM-dd"),
        format(new Date(), "yyyy-MM-dd")
      ],
      showDatePicker: false,
      networkStatus: "all",
      accountStatus: "all",
      facilities: [],
      rawFacilitiesResult: [],
      searchClicked: false,

      accountStatuses: [
        { name: "Basic", value: "basic" },
        { name: "Premium", value: "unlimited_leads" }
      ],

      premiumSetting: "all",
      premiumSettings: [
        { name: "Unlimited Leads", value: "unlimited_leads" },
        { name: "Premium Calls", value: "premium_calls" },
        { name: "Premium Clicks", value: "premium_clicks" }
      ],

      networkStatuses: [
        { name: "All", value: "all" },
        { name: "In Network", value: "in_network" },
        { name: "Out of network", value: "out_of_network" }
      ],

      headers: [
        { text: "Provider ID", value: "companyId", sortable: false },
        { text: "Provider Name", value: "companyName", sortable: false },
        { text: "Listing ID", value: "facilityId" },
        { text: "Listing Name", value: "name" },
        { text: "Unique Users", value: "uniqueUsers", sortable: false },
        { text: "Page Views", value: "pageViews", sortable: false },
        { text: "Premium Clicks", value: "premiumClicks", sortable: false },
        {
          text: "Today's Premium Click Price",
          value: "premiumClickPrice",
          sortable: false
        },
        {
          text: "Average Premium Click Price",
          value: "averagePremiumClickPrice",
          sortable: false
        },
        { text: "Premium Calls", value: "premiumCalls", sortable: false },
        {
          text: "Today's Premium Call Price",
          value: "premiumCallPrice",
          sortable: false
        },
        {
          text: "Average Premium Call Price",
          value: "averagePremiumCallPrice",
          sortable: false
        },
        { text: "Monthly Upgrade", value: "subscription", sortable: false },
        {
          text: "Monthly Upgrade Fee",
          value: "subscriptionPrice",
          sortable: false
        },
        { text: "Listing Type", value: "premiumStatus", sortable: false },
        {
          text: "Organic Match Referrals",
          value: "matchReferrals",
          sortable: false
        },
        {
          text: "Days Without Activity",
          value: "daysSinceLastActivity",
          sortable: false
        },
        {
          text: "Match Forms (Ad)",
          value: "matchFeaturedListingLeads",
          sortable: false
        },
        {
          text: "Helpline Call Leads",
          value: "helplineCalls",
          sortable: false
        },
        { text: "Listing Forms", value: "getHelpReferrals", sortable: false },
        {
          text: "Helpline Calls",
          value: "helplineCallsReferrals",
          sortable: false
        },
        { text: "Free Clicks", value: "contactUsClicks", sortable: false },
        { text: "Free Calls", value: "contactUsCalls", sortable: false },
        { text: "Free Value", value: "freeValue", sortable: false }
      ],
      paginationOptions: {
        itemsPerPage: 10,
        page: 1,
        sortBy: ["name"],
        sortDesc: [false]
      },

      downloadLabels: {
        companyId: "Provider ID",
        companyName: "Provider Name",
        facilityId: "Listing ID",
        name: "Listing Name",
        uniqueUsers: "Unique Users",
        pageViews: "Page Views",
        premiumClicks: "Premium Clicks",
        premiumClickPrice: "Today's Premium Click Price",
        averagePremiumClickPrice: "Average Premium Click Price",
        premiumCalls: "Premium Calls",
        averagePremiumCallPrice: "Average Premium Call Price",
        subscription: "Monthly Upgrade",
        subscriptionPrice: "Monthly Upgrade Fee",
        premiumStatus: "Listing Type",
        daysSinceLastActivity: "Days Without Activity",
        matchReferrals: "Organic Match Referrals",
        matchFeaturedListingLeads: "Match Forms (Ad)",
        helplineCalls: "Helpline Call Leads",
        getHelpReferrals: "Listing Forms",
        helplineCallsReferrals: "Healpline Calls",
        contactUsClicks: "Free Clicks",
        contactUsCalls: "Free Calls",
        freeValue: "Free Value"
      }
    };
  },

  components: {
    JsonCSV
  },

  computed: {
    dateRangeText() {
      return this.dates.join(" – ");
    },
    searchPremiumStatus() {
      if (this.accountStatus === "all" || this.accountStatus === "basic") {
        return this.accountStatus;
      }
      return this.premiumSetting;
    },
    filename() {
      if (this.facilitySearchText) {
        return this.facilitySearchText;
      } else if (this.companySearchText) {
        return this.companySearchText;
      }

      return "databridge";
    },

    csvData() {
      const facilities = JSON.parse(JSON.stringify(this.facilities));
      return facilities.map(facility => {
        delete facility["contactUsClickData"];
        delete facility["contactUsCallData"];
        return facility;
      });
    }
  },

  watch: {
    accountStatus: function(newVal) {
      if (newVal !== "all") {
        this.networkStatus = "in_network";
      }

      if (newVal === "basic") {
        this.premiumSetting = "all";
        this.accountStatus = "basic";
      }

      if (newVal === "unlimited_leads" && this.premiumSetting === "all") {
        this.premiumSetting = "unlimited_leads";
      }
    },
    premiumSetting: function(newVal) {
      if (newVal && newVal !== "" && newVal !== "all") {
        this.accountStatus = "unlimited_leads";
      }
    },
    networkStatus: function(newVal) {
      if (newVal === "all" || newVal === "out_of_network") {
        this.premiumSetting = "all";
        this.accountStatus = "all";
      }
    }
  },

  methods: {
    parseContactUsClicks(contactUsClicks) {
      const parsedData = JSON.parse(contactUsClicks.data.googleAnalytics);

      // console.log('contactUsClicks', JSON.parse(JSON.stringify(parsedData)));

      for (const facility of this.facilities) {
        const clickData = parsedData.facilityInteractions.find(
          item => parseInt(item.facilityId) === parseInt(facility.facilityId)
        );
        facility.contactUsClicks = clickData.interactions.length;
        facility.contactUsClickData = clickData;
      }
    },

    parseContactUsCalls(contactUsCalls) {
      const parsedData = JSON.parse(contactUsCalls.data.googleAnalytics);

      // console.log('contactUsCalls', JSON.parse(JSON.stringify(parsedData)));

      for (const facility of this.facilities) {
        const callData = parsedData.facilityInteractions.find(
          item => parseInt(item.facilityId) === parseInt(facility.facilityId)
        );
        facility.contactUsCalls = callData.interactions.length;
        facility.contactUsCallData = callData;
      }
    },

    parseGAUsers(gaUsers) {
      const parsedData = JSON.parse(gaUsers.data.googleAnalytics);

      // console.log('GA USERS', JSON.parse(JSON.stringify(parsedData)));

      for (const facility of this.facilities) {
        const userData = parsedData.facilityInteractions.find(
          item => parseInt(item.facilityId) === parseInt(facility.facilityId)
        );
        facility.uniqueUsers =
          userData && userData.interactions ? userData.interactions.length : 0;
      }
    },

    parseGAPageViews(gaData) {
      const parsedData = JSON.parse(gaData.data.googleAnalytics);

      console.log("GA PAGE VIEWS", JSON.parse(JSON.stringify(parsedData)));

      for (const facility of this.facilities) {
        const data = parsedData.facilityInteractions.find(
          item => parseInt(item.facilityId) === parseInt(facility.facilityId)
        );
        facility.pageViews = data.interactions.length;
      }
    },

    parseGaHelplineCalls(gaData) {
      const parsedData = JSON.parse(gaData.data.googleAnalytics);

      // console.log('GA HELPLINE CALLS', JSON.parse(JSON.stringify(parsedData)));

      for (const facility of this.facilities) {
        const data = parsedData.facilityInteractions.find(
          item => parseInt(item.facilityId) === parseInt(facility.facilityId)
        );

        let count = 0;

        if (data.interactions.length) {
          const rawFacility = this.rawFacilitiesResult.facilities.find(
            rawFacility => rawFacility.externalId === facility.facilityId
          );

          // IF NOT SUBSCRIPTION ACTIVE, JUST RETURN THE NUMBER OF RESULTS
          if (
            !rawFacility ||
            !rawFacility.connectFacility ||
            !rawFacility.connectFacility.subscription ||
            !rawFacility.connectFacility.subscription.active
          ) {
            facility.helplineCallsReferrals = data.interactions.length;
            continue;
          }

          // ONLY COUNT INTERACTIONS FROM BEFORE SUBSCRIPTION START DATE.
          for (const interaction of data.interactions) {
            // console.log('INTERACTION', JSON.parse(JSON.stringify(interaction)));
            const subscriptionStart = parseISO(
              rawFacility.connectFacility.subscription.startDate
            );
            console.log("subscription start", subscriptionStart);

            const interactionDate = parseISO(interaction.date + " 00:00:00");
            // console.log('INTERACTION DATE', interactionDate);
            if (isBefore(interactionDate, subscriptionStart)) {
              count++;
            }
          }
        }
        facility.helplineCallsReferrals = count;
      }
    },
    methods: {
      async searchReport() {
        this.offset = 0;
        this.paginationOptions.page = 1;

        await this.searchReport();
        this.awaitingSearch = false;
      },

      updatePaginationOptions(paginationOptions) {
        this.page = paginationOptions.page - 1;
        this.size = paginationOptions.itemsPerPage;

        this.getCompanies();
      },

      async getReport() {
        try {
          this.loading = true;
          const result = await getGlobalReport(
            this.page,
            this.size,
            this.dates[0],
            this.dates[1],
            this.search
          );
          this.serverItemsLength = result.total;
          this.facilities = result.items;
          this.loading = false;
        } catch (e) {
          this.loading = false;
          console.error(e);
        }
      }
    }
  }
};
</script>

<style scoped></style>
